import { distance, lerpPoint, type Complex } from "./complex";

export type Cubic = Readonly<{
  from: Complex;
  c1: Complex;
  c2: Complex;
  to: Complex;
}>;
export type Outline = readonly Cubic[];

export const cubicPoint = (c: Cubic, t: number): Complex => {
  const u = 1 - t;
  return {
    x:
      u ** 3 * c.from.x +
      3 * u * u * t * c.c1.x +
      3 * u * t * t * c.c2.x +
      t ** 3 * c.to.x,
    y:
      u ** 3 * c.from.y +
      3 * u * u * t * c.c1.y +
      3 * u * t * t * c.c2.y +
      t ** 3 * c.to.y,
  };
};

/** Dense length lookup followed by linear interpolation. The closing point is NOT duplicated. */
export const samplePath = (
  outline: Outline,
  count = 1024,
  subdivisions = 2048,
): Complex[] => {
  if (
    !outline.length ||
    !Number.isInteger(count) ||
    count < 2 ||
    !Number.isInteger(subdivisions) ||
    subdivisions < 2
  ) {
    throw new Error(
      "A closed outline and at least two samples/subdivisions are required.",
    );
  }
  for (let i = 0; i < outline.length; i++) {
    const segment = outline[i];
    if (
      ![segment.from, segment.c1, segment.c2, segment.to].every(
        (p) => Number.isFinite(p.x) && Number.isFinite(p.y),
      )
    ) {
      throw new Error("Outline contains non-finite coordinates.");
    }
    if (distance(segment.to, outline[(i + 1) % outline.length].from) > 1e-8)
      throw new Error("Outline must be closed and continuous.");
  }
  const dense: Complex[] = [outline[0].from];
  for (const curve of outline) {
    for (let i = 1; i <= subdivisions; i++)
      dense.push(cubicPoint(curve, i / subdivisions));
  }
  const lengths = [0];
  for (let i = 1; i < dense.length; i++)
    lengths.push(lengths[i - 1] + distance(dense[i], dense[i - 1]));
  const total = lengths[lengths.length - 1];
  if (total <= 0) throw new Error("Outline has zero length.");
  let cursor = 1;
  return Array.from({ length: count }, (_, i) => {
    const target = (total * i) / count;
    while (lengths[cursor] < target) cursor++;
    const span = lengths[cursor] - lengths[cursor - 1];
    return lerpPoint(
      dense[cursor - 1],
      dense[cursor],
      span === 0 ? 0 : (target - lengths[cursor - 1]) / span,
    );
  });
};

/** Remove the centroid, then fit the longest bounding-box dimension to two units. */
export const normalizeOutline = (points: readonly Complex[]): Complex[] => {
  if (
    points.length < 2 ||
    !points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
  )
    throw new Error("Invalid points.");
  const center = points.reduce(
    (a, p) => ({ x: a.x + p.x / points.length, y: a.y + p.y / points.length }),
    { x: 0, y: 0 },
  );
  const extent = Math.max(
    Math.max(...points.map((p) => p.x)) - Math.min(...points.map((p) => p.x)),
    Math.max(...points.map((p) => p.y)) - Math.min(...points.map((p) => p.y)),
  );
  if (extent === 0) throw new Error("Outline has zero extent.");
  return points.map((p) => ({
    x: ((p.x - center.x) * 2) / extent,
    y: ((p.y - center.y) * 2) / extent,
  }));
};
